import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFellows } from '../utils/api';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, UserCircleIcon, AcademicCapIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const PROJECTS = ['All', 'Y-Check', 'Neotree', 'KOSHESAI', 'PROMISE', 'Fractures E3', 'Helen Jack'];

const PROJECT_COLORS = {
  'Y-Check':      'bg-blue-900/60 text-blue-300',
  'Neotree':      'bg-green-900/60 text-green-300',
  'KOSHESAI':     'bg-purple-900/60 text-purple-300',
  'PROMISE':      'bg-amber-900/60 text-amber-300',
  'Fractures E3': 'bg-red-900/60 text-red-300',
  'Helen Jack':   'bg-teal-900/60 text-teal-300',
};

export default function FellowsPage() {
  const [search, setSearch] = useState('');
  const [activeProject, setActiveProject] = useState('All');

  const { data, isLoading } = useQuery({
    queryKey: ['fellows', search],
    queryFn: () => getFellows({ search: search || undefined }),
  });

  const allFellows = data?.data || [];
  const fellows = activeProject === 'All'
    ? allFellows
    : allFellows.filter(f => f.thru_project === activeProject);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-white">MSc Fellows Directory</h1>
        <p className="text-gray-500 text-sm">{fellows.length} fellow{fellows.length !== 1 ? 's' : ''}{activeProject !== 'All' ? ` in ${activeProject}` : ''}</p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
        <input
          className="input pl-9"
          placeholder="Search by name, project, university..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* THRU Project Tabs */}
      <div className="flex gap-2 flex-wrap">
        {PROJECTS.map(p => (
          <button
            key={p}
            onClick={() => setActiveProject(p)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
              activeProject === p
                ? 'bg-primary-600 border-primary-500 text-white'
                : 'bg-surface-100 border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'
            }`}
          >
            {p}
            {p !== 'All' && (
              <span className="ml-1.5 text-xs opacity-70">
                {allFellows.filter(f => f.thru_project === p).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading fellows...</div>
      ) : fellows.length === 0 ? (
        <div className="card text-center py-12">
          <UserCircleIcon className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No fellows found.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fellows.map((profile) => (
            <Link key={profile.id} to={`/fellows/${profile.user_id}`} className="card hover:border-gray-700 transition-all block">
              <div className="flex items-start gap-3">
                {profile.user?.avatar_url ? (
                  <img src={profile.user.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary-600/30 flex items-center justify-center text-primary-400 text-lg font-bold flex-shrink-0">
                    {profile.user?.full_name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white text-sm truncate">{profile.user?.full_name}</div>
                  <div className="text-gray-500 text-xs truncate">{profile.degree || 'MSc Fellow'}</div>
                  <div className="text-gray-600 text-xs truncate">{profile.university}</div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-800 space-y-1.5">
                {profile.thru_project && (
                  <span className={`badge text-xs ${PROJECT_COLORS[profile.thru_project] || 'bg-gray-800 text-gray-400'}`}>
                    {profile.thru_project}
                  </span>
                )}
                {profile.project_title && (
                  <div className="flex items-start gap-2 text-xs text-gray-400 mt-1">
                    <AcademicCapIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-600" />
                    <span className="line-clamp-2">{profile.project_title}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-2">
                  <span className={`badge ${profile.current_status === 'Active' ? 'bg-green-900/50 text-green-300' : 'bg-gray-800 text-gray-400'}`}>
                    {profile.current_status || 'Active'}
                  </span>
                  {profile.expected_completion && (
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <ClockIcon className="w-3 h-3" />
                      {format(new Date(profile.expected_completion), 'MMM yyyy')}
                    </span>
                  )}
                </div>
                {profile.location && (
                  <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                    <MapPinIcon className="w-3 h-3" />
                    {profile.location}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
